import { useToken } from "@gluestack-style/react";
import EquipmentTable from "components/equipment/EquipmentTable";
import { SiteSelect } from "components/form/SiteSelect";
import Container from "components/general/Container";
import DismissableContent from "components/general/DismissableContent";
import Section from "components/general/Section";
import { Box, Button, H3, HStack, ParagraphSmall, Textarea, TextareaInput, Typography } from "components/ui";
import { Link, router } from "expo-router";
import { Formik } from "formik";
import { AlertCircle, AlertTriangle, ArrowLeft, ArrowRight, Check, SendHorizonal, Wrench } from "lucide-react-native";
import React, { useEffect, useState } from "react";
import { ActivityIndicator, Pressable } from "react-native";
import { useGetEquipmentQuery, usePostServiceOrderMutation } from "store/abcoApi";
import { Value } from "types/select.types";
import { useSiteRegion } from "utils/hooks/useSiteSection";
import * as Yup from "yup";

interface SiteValue extends Value {
	siteId: number;
}

const InputError = ({ text }: { text: string }) => {
	const resolvedErrorBright = useToken("colors", "errorbright");
	return (
		<HStack gap={6} py={6}>
			<AlertCircle color={resolvedErrorBright} />
			<Typography.ParagraphSmall sx={{ color: "$errorbright" }}>{text}</Typography.ParagraphSmall>
		</HStack>
	);
};

interface FormValues {
	site: SiteValue | null;
	selectedEquipment: number[];
	serviceType: "Service Call - Breakdown (72 Hrs)" | "Service Call - General Maintenance (30 Days)" | "Service Call - Urgent Breakdown (8 Hrs)" | "";
	notes: string;
}

interface BookAServiceFormProps {
	// Remove site prop as it will be selected in step 1
}

const validationSchema = Yup.object().shape({
	site: Yup.object().nullable().required("Please select a site"),
	selectedEquipment: Yup.array().min(1, "Please select at least one equipment"),
	serviceType: Yup.string().required("Please select a service type"),
	notes: Yup.string().required("Please provide notes about the service"),
});

const initialValues: FormValues = {
	site: null,
	selectedEquipment: [],
	serviceType: "",
	notes: "",
};

export default function BookAServiceForm({}: BookAServiceFormProps) {
	const [currentStep, setCurrentStep] = useState(1);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isValidating, setIsValidating] = useState(false);
	const [selectedSite, setSelectedSite] = useState<SiteValue | null>(null);
	const [createServiceBooking] = usePostServiceOrderMutation();
	const totalSteps = 3;
	const region = useSiteRegion();

	const { data: equipmentData } = useGetEquipmentQuery(
		{
			pageSize: 9999,
			siteIdCollection: selectedSite?.siteId ? [selectedSite.siteId] : [],
			isHire: false,
		},
		{
			skip: !selectedSite?.siteId,
		},
	);

	useEffect(() => {
		if (initialValues.site) {
			setSelectedSite(initialValues.site);
		}
	}, []);

	const handleNext = async (formik: any) => {
		if (isValidating) return; // Prevent double clicks

		setIsValidating(true);

		const currentStepFields = currentStep === 1 ? ["site"] : currentStep === 2 ? ["selectedEquipment"] : ["serviceType", "notes"];

		const touched = currentStepFields.reduce(
			(acc, field) => ({
				...acc,
				[field]: true,
			}),
			{},
		);

		await formik.setTouched(touched);

		await new Promise((resolve) => setTimeout(resolve, 100));

		const errors = await formik.validateForm();
		const hasStepErrors = currentStepFields.some((field) => errors[field]);

		if (!hasStepErrors) {
			setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
		}

		setIsValidating(false);
	};

	const handleBack = () => {
		setCurrentStep((prev) => Math.max(prev - 1, 1));
	};

	const handleSubmit = async (values: FormValues, formikBag: any) => {
		const errors = await formikBag.validateForm();
		const hasErrors = Object.keys(errors).length > 0;

		if (hasErrors) {
			formikBag.setTouched({
				site: true,
				selectedEquipment: true,
				serviceType: true,
				notes: true,
			});
			return;
		}

		setIsSubmitting(true);
		try {
			createServiceBooking({
				createServiceOrderRequest: {
					problemDescription: values.notes,
					equipmentCollection: values.selectedEquipment,
					siteId: values.site?.siteId,
					serviceType: values.serviceType,

					// equipment: serviceBookingPayload.equipment,
					// service: serviceBookingPayload.service,
					// notes: serviceBookingPayload.notes,
					// metadata: serviceBookingPayload.metadata
				},
			}).then((response: any) => {
				router.push(`/equipment/service-orders/${response.data.id}`);
			});

			console.log("✅ Service booking submitted successfully!");
			// alert(
			// 	`Service booked successfully!\n\nService Type: ${values.serviceType === "planned" ? "Planned Maintenance" : "Breakdown Service"}\nEquipment: ${values.selectedEquipment.length} item(s) selected\nSite: ${values.site?.name}\n\nCheck console for full details.`,
			// );

			// formikBag.resetForm();
			// setSelectedSite(null);
			// setCurrentStep(1);
		} catch (error) {
			console.error("❌ Error booking service:", error);
			alert("Error booking service. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	const ServiceButton = ({ type, title, description, price, isSelected, onPress, icon }: { type: string; title: string; description: string; price: string; isSelected: boolean; onPress: () => void; icon: React.ReactNode }) => {
		const borderColor = isSelected ? "$primarymain" : "$neutral300";
		const backgroundColor = isSelected ? "$primarylightbg" : "$basicwhite";

		return (
			<Box flex={1}>
				<Button
					width="100%"
					variant="outline"
					onPress={onPress}
					gap={24}
					sx={{
						borderColor,
						backgroundColor,
						borderWidth: 1,
						padding: 16,
						borderRadius: 12,
					}}
				>
					{icon}
					<Box gap={4}>
						<HStack gap={8} alignItems="center">
							<Typography weight="bold" color={isSelected ? "$primary" : "$basicblack"}>
								{title}
							</Typography>
						</HStack>
						<Typography.ParagraphSmall color={isSelected ? "$primary" : "$basicblack"}>{description}</Typography.ParagraphSmall>
						<Typography fontWeight="bold" color={isSelected ? "$primary" : "$basicblack"}>
							{price}
						</Typography>
					</Box>
				</Button>
			</Box>
		);
	};

	if (region === "nz") {
		return (
			<Box justifyContent="space-between" height={"$full"}>
				<Container>
					<Typography color="$textsecondary" mb={24}>
						Please use our form to book a service.
					</Typography>
					<Link
						href="https://app.smartsheet.com/b/form/fef4d7d595c2420ca5bad0a506914d88"
						target="_blank"
						style={{
							textDecorationLine: "underline",
							color: "#1A428A",
						}}
					>
						Book a service
					</Link>
				</Container>
			</Box>
		);
	}

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values, formikBag) => handleSubmit(values, formikBag)}>
			{(formik) => (
				<Box justifyContent="space-between" height={"$full"}>
					{/* Step Indicator */}
					<Box position="relative" zIndex={10}>
						<Container>
							<HStack justifyContent="center" alignItems="center" mb={32}>
								{[1, 2, 3].map((step, idx, arr) => {
									const isCompleted = step < currentStep;
									const isCurrent = step === currentStep;
									const StepCircle = (
										<Box
											width={32}
											height={32}
											borderRadius={16}
											borderWidth={2}
											borderColor={isCurrent ? "$primary" : isCompleted ? "$primary" : "$neutral400"}
											alignItems="center"
											justifyContent="center"
											bg={isCurrent ? "$primary" : isCompleted ? "$basicwhite" : "$basicwhite"}
										>
											{isCompleted ? <Check size={16} color="#1A428A" /> : <Typography.H5 color={isCurrent ? "$basicwhite" : isCompleted ? "$primary" : "$neutral600"}>{step}</Typography.H5>}
										</Box>
									);
									return (
										<React.Fragment key={step}>
											{isCompleted ? <Pressable onPress={() => setCurrentStep(step)}>{StepCircle}</Pressable> : StepCircle}
											{idx < arr.length - 1 && <Box flex={1} height={2} bg="$neutral300" mx={2} />}
										</React.Fragment>
									);
								})}
							</HStack>
						</Container>

						{/* Step 1: Site Selection */}
						{currentStep === 1 && (
							<Section gap={24}>
								<Box gap={24}>
									<Box gap={12}>
										<H3>Select a Site</H3>
										<Typography color="$neutral800">Choose the site where the service will be performed</Typography>
									</Box>
									<SiteSelect
										isLight
										onSelect={(site: SiteValue | null) => {
											formik.setFieldValue("site", site);
											setSelectedSite(site);
										}}
										value={formik.values.site || undefined}
									/>
									{formik.touched.site && formik.errors.site && <InputError text={String(formik.errors.site)} />}
								</Box>
							</Section>
						)}

						{/* Step 2: Equipment Selection */}
						{currentStep === 2 && (
							<Section gap={24}>
								<Box gap={12}>
									{/* <H3>Select Equipment</H3>
									<Text color="$textsecondary">Choose the equipment that needs service</Text> */}

									{formik.values.site ? (
										equipmentData?.list && equipmentData.list.length > 0 ? (
											<EquipmentTable
												dataList={equipmentData.list}
												selectable={true}
												selectedEquipment={formik.values.selectedEquipment}
												onSelectionChange={(ids) => {
													formik.setFieldValue("selectedEquipment", ids);
												}}
											/>
										) : (
											<Box gap={24}>
												<Box flex={2} gap={12}>
													<H3>No equipment found.</H3>
													<ParagraphSmall sx={{ lineHeight: 21 }} weight="normal">
														This page keeps track of all purchased equipment — acting as your team's asset register.
													</ParagraphSmall>
													<ParagraphSmall sx={{ lineHeight: 21 }} weight="normal">
														Right now, it's empty — once purchases are made, everything will appear here automatically.
													</ParagraphSmall>
												</Box>
												<Box flex={1} width="$full">
													<Button isLink borderRadius={8} variant="primary-light" href="/equipment">
														<ParagraphSmall sx={{ color: "$basicwhite" }}>Browse Equipment</ParagraphSmall>
													</Button>
												</Box>
											</Box>
										)
									) : (
										<Typography color="$textsecondary">Please select a site first</Typography>
									)}

									{formik.touched.selectedEquipment && formik.errors.selectedEquipment && (
										<Box width="$full">
											<DismissableContent icon={null} variant="error">
												<InputError text={String(formik.errors.selectedEquipment)} />
											</DismissableContent>
										</Box>
									)}
								</Box>
							</Section>
						)}

						{/* Step 3: Service Type and Notes */}
						{currentStep === 3 && (
							<Section gap={24}>
								<Box gap={12}>
									<H3 color={formik.touched.serviceType && formik.errors.serviceType ? "$errorbright" : "$textprimary"}>Service type *</H3>
									<Typography color="$textsecondary">Select the type of service being performed</Typography>

									<HStack
										gap={12}
										borderColor={formik.touched.serviceType && formik.errors.serviceType ? "$errorbright" : "transparent"}
										borderWidth={formik.touched.serviceType && formik.errors.serviceType ? 1 : 0}
										borderRadius={12}
										padding={formik.touched.serviceType && formik.errors.serviceType ? 8 : 0}
									>
										<ServiceButton
											type="planned"
											title="Planned maintenance"
											description="Service within 30 days"
											price="$220 + GST"
											isSelected={formik.values.serviceType === "Service Call - General Maintenance (30 Days)"}
											onPress={() => {
												formik.setFieldValue("serviceType", "Service Call - General Maintenance (30 Days)");
											}}
											icon={<Wrench size={46} color={formik.values.serviceType === "Service Call - General Maintenance (30 Days)" ? "#1A428A" : "black"} />}
										/>

										<ServiceButton
											type="breakdown"
											title="Breakdown"
											description="Service within 72 hours"
											price="$275 + GST"
											isSelected={formik.values.serviceType === "Service Call - Breakdown (72 Hrs)"}
											onPress={() => {
												formik.setFieldValue("serviceType", "Service Call - Breakdown (72 Hrs)");
											}}
											icon={<AlertTriangle size={46} color={formik.values.serviceType === "Service Call - Breakdown (72 Hrs)" ? "#1A428A" : "black"} />}
										/>

										<ServiceButton
											type="urgent-breakdown"
											title="Urgent Breakdown"
											description="Service within 8 hours"
											price="$399 + GST"
											isSelected={formik.values.serviceType === "Service Call - Urgent Breakdown (8 Hrs)"}
											onPress={() => {
												formik.setFieldValue("serviceType", "Service Call - Urgent Breakdown (8 Hrs)");
											}}
											icon={<AlertTriangle size={46} color={formik.values.serviceType === "Service Call - Urgent Breakdown (8 Hrs)" ? "#1A428A" : "black"} />}
										/>
									</HStack>

									{formik.touched.serviceType && formik.errors.serviceType && <InputError text={formik.errors.serviceType} />}

									<Typography sx={{ fontSize: 13, color: "$texttertiary" }}>Price is call out fee and up to 1hr on site. Additional labour, parts and accessories will be charged.</Typography>
								</Box>

								<Box gap={12}>
									<H3 color={formik.touched.notes && formik.errors.notes ? "$errorbright" : "$textprimary"}>Notes *</H3>
									<Typography color="$textsecondary">Provide any additional details such as current issues.</Typography>
									<Textarea borderColor={formik.touched.notes && formik.errors.notes ? "$errorbright" : "$neutral300"} borderRadius={12} padding={12} sx={{ minHeight: 120 }}>
										<TextareaInput value={formik.values.notes} onChangeText={(text) => formik.setFieldValue("notes", text)} placeholder="Additional details" onBlur={() => formik.setFieldTouched("notes", true)} />
									</Textarea>
									{formik.touched.notes && formik.errors.notes && <InputError text={formik.errors.notes} />}
								</Box>
							</Section>
						)}
					</Box>

					{/* Navigation Buttons */}
					<HStack mb={48} position="relative" zIndex={6} justifyContent="space-between" mt={24}>
						{currentStep > 1 && (
							<Button gap={4} variant="secondary" onPress={handleBack}>
								<ArrowLeft />
								<Typography weight="bold">Previous</Typography>
							</Button>
						)}
						{currentStep < totalSteps ? (
							<Button gap={4} ml="auto" variant="secondary" onPress={() => handleNext(formik)}>
								<Typography weight="bold">Next</Typography>
								<ArrowRight />
							</Button>
						) : (
							<Button
								gap={12}
								variant="primary-light"
								borderRadius={12}
								onPress={() => {
									// Touch service type field to show validation if not selected
									if (!formik.values.serviceType) {
										formik.setFieldTouched("serviceType", true);
									}
									if (!formik.values.notes) {
										formik.setFieldTouched("notes", true);
									}
									formik.handleSubmit();
								}}
								disabled={isSubmitting}
								sx={{
									opacity: isSubmitting ? 0.7 : 1,
									minWidth: 200,
								}}
							>
								<Typography color="white" fontWeight="bold">
									{isSubmitting ? "Booking Service..." : "Book a Service"}
								</Typography>
								{isSubmitting ? <ActivityIndicator color={"white"} style={{ transform: [{ scale: 1.5 }] }} /> : <SendHorizonal color="white" size={20} />}
							</Button>
						)}
					</HStack>
				</Box>
			)}
		</Formik>
	);
}
